import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import CONFIG from '../../app-configs'
import { ApiAuthStrategies } from '../shared/ApiAuthStrategies'
import {
  type AccountChargeExclusionDto,
  type CreateAccountChargeExclusionPayload
} from '../../api-schema-types/asset-holdings/AccountChargeExclusionDtos'

export type AccountChargeRunReportName = 'members' | 'charges'

export interface DownloadAccountChargeRunReportArgs {
  chargeMonth: string
  reportName: AccountChargeRunReportName
}

export const accountChargeRunApi = createApi({
  reducerPath: 'accountChargeRunApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${CONFIG.PROXIMA_BACK_END_V2_BASE_API_URL}/asset-holdings/account-charges`,
    prepareHeaders: (headers: Headers) => {
      headers.set('authorization', ApiAuthStrategies.getBearerTokenHeader())
      return headers
    }
  }),
  tagTypes: ['AccountChargeExclusions'],

  endpoints: (builder) => ({
    downloadReportCsv: builder.query<{ csv: string, filename: string }, DownloadAccountChargeRunReportArgs>({
      query: ({ chargeMonth, reportName }) => ({
        url: `/runs/${chargeMonth}/${reportName}.csv`,
        method: 'GET',
        responseHandler: async (response: Response) => {
          const csv = await response.text()
          const filename = response.headers.get('X-Proxima-CSV-filename') ?? `account-charge-run-${reportName}-${chargeMonth}.csv`
          return { csv, filename }
        }
      })
    }),

    getExclusions: builder.query<AccountChargeExclusionDto[], undefined>({
      query: () => ({ url: '/exclusions', method: 'GET' }),
      providesTags: ['AccountChargeExclusions']
    }),

    createExclusion: builder.mutation<{ id: number }, CreateAccountChargeExclusionPayload>({
      query: (payload) => ({ url: '/exclusions', method: 'POST', body: payload }),
      invalidatesTags: ['AccountChargeExclusions']
    }),

    removeExclusion: builder.mutation<undefined, number>({
      query: (exclusionId) => ({ url: `/exclusions/${exclusionId}`, method: 'DELETE' }),
      invalidatesTags: ['AccountChargeExclusions']
    })
  })
})

export const useDownloadAccountChargeRunReportCsvLazyQuery = accountChargeRunApi.endpoints.downloadReportCsv.useLazyQuery
export const useGetAccountChargeExclusionsQuery = accountChargeRunApi.endpoints.getExclusions.useQuery
export const useCreateAccountChargeExclusionMutation = accountChargeRunApi.endpoints.createExclusion.useMutation
export const useRemoveAccountChargeExclusionMutation = accountChargeRunApi.endpoints.removeExclusion.useMutation
